import React, { useEffect, useMemo, useState } from 'react';
import GroceryIcon from '../../components/grocery/GroceryIcon';

const API = (typeof window !== 'undefined' ? window.location.origin : '');
const LANGS = ['en', 'fr', 'es'];

// The five newest emoji in the catalog: if a device lacks the bundled image these
// are the ones that would show as tofu, so they get their own row.
const NEWEST = [
    { file: '1fad7.png', label: 'Pouring liquid (U+1FAD7)' },
    { file: '1fadb.png', label: 'Pea pod (U+1FADB)' },
    { file: '1fada.png', label: 'Ginger (U+1FADA)' },
    { file: '1fad9.png', label: 'Jar (U+1FAD9)' },
    { file: '1fad8.png', label: 'Beans (U+1FAD8)' },
];

const Tile = ({ name, icon, badge, note, status, testId }) => (
    <div className="flex flex-col items-center gap-1.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-2.5 text-center"
        data-testid={testId}>
        <GroceryIcon icon={icon} badge={badge} alt={name} size={44} />
        <span className="text-xs font-medium leading-tight text-slate-800 dark:text-slate-200">{name}</span>
        {note ? <span className="text-[10px] uppercase tracking-wide text-slate-400">{note}</span> : null}
        {status && status !== 'direct'
            ? <span className={`text-[10px] font-semibold ${status === 'symbolic' ? 'text-amber-600' : 'text-sky-600'}`}>{status}</span>
            : null}
    </div>
);

export default function GroceryIconAtlasPage() {
    const [data, setData] = useState(null);
    const [lang, setLang] = useState('en');
    const [audit, setAudit] = useState(null);

    useEffect(() => {
        fetch(`${API}/api/grocery/catalog/atlas`).then((r) => r.json()).then(setData).catch(() => setData({ error: true }));
    }, []);

    // Every list reads A→Z in the customer's language: accent-insensitive, numeric-aware.
    const collator = useMemo(() => new Intl.Collator(lang, { sensitivity: 'base', numeric: true }), [lang]);
    const sorted = (rows) => [...(rows || [])].sort((a, b) => collator.compare(a.name[lang], b.name[lang]));

    const runAudit = () => {
        const imgs = Array.from(document.querySelectorAll('img[data-grocery-icon], img[data-grocery-badge]'));
        const broken = imgs.filter((i) => !i.complete || i.naturalWidth === 0)
            .map((i) => i.getAttribute('data-grocery-icon') || i.getAttribute('data-grocery-badge'));
        const missingSrc = imgs.filter((i) => !i.getAttribute('src')).length;
        setAudit({ images: imgs.length, broken, missingSrc, at: new Date().toISOString() });
    };

    useEffect(() => {
        if (!data || data.error) return;
        const t = setTimeout(runAudit, 2500);
        return () => clearTimeout(t);
    }, [data]);

    if (!data) return <div className="p-10 text-slate-500" data-testid="grocery-atlas-loading">Loading the master catalog…</div>;
    if (data.error) return <div className="p-10 text-red-600" data-testid="grocery-atlas-error">The master catalog could not be read.</div>;

    const badgeCount = data.items.filter((i) => i.badge).length;

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 px-4 py-8 sm:px-8" data-testid="grocery-icon-atlas">
            <header className="mx-auto max-w-6xl">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">Internal audit page · PREVIEW</p>
                <h1 className="mt-1 text-3xl font-black text-slate-900 dark:text-white sm:text-4xl">Grocery icon atlas</h1>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                    Every tile below draws a <strong>bundled</strong> Noto Color Emoji PNG (128 px, one source) from
                    <code className="mx-1 rounded bg-slate-200 px-1 dark:bg-slate-800">{data.icon_base}</code>.
                    No CDN is contacted at runtime and the device's emoji font is never used.
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                    {LANGS.map((l) => (
                        <button key={l} onClick={() => setLang(l)} data-testid={`grocery-atlas-lang-${l}`}
                            className={`min-h-[40px] rounded-full px-4 text-sm font-semibold transition-colors ${lang === l ? 'bg-emerald-600 text-white' : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-200 dark:border-slate-700'}`}>
                            {l.toUpperCase()}
                        </button>
                    ))}
                    <button onClick={runAudit} data-testid="grocery-atlas-recheck"
                        className="min-h-[40px] rounded-full bg-slate-900 px-4 text-sm font-semibold text-white hover:bg-slate-700">
                        Re-check every image
                    </button>
                </div>

                <dl className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="grocery-atlas-counts">
                    {[['Aisles', data.counts.aisles], ['Sections', data.counts.sections],
                    ['Items', data.counts.items], ['Option groups', data.counts.option_groups],
                    ['Items with a badge', badgeCount]].map(([k, v]) => (
                        <div key={k} className="rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
                            <dt className="text-[11px] uppercase tracking-wide text-slate-500">{k}</dt>
                            <dd className="text-2xl font-black text-slate-900 dark:text-white">{v}</dd>
                        </div>
                    ))}
                </dl>

                <div className={`mt-4 rounded-xl border p-4 ${audit && audit.broken.length === 0 ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30' : 'border-amber-300 bg-amber-50 dark:bg-amber-950/30'}`}
                    data-testid="grocery-atlas-audit">
                    {!audit ? <span className="text-sm text-slate-600">Checking every image…</span> : (
                        <>
                            <p className="text-sm font-bold text-slate-900 dark:text-white">
                                {audit.broken.length === 0
                                    ? `All ${audit.images} images loaded — no blank or broken tile.`
                                    : `${audit.broken.length} of ${audit.images} images failed to load.`}
                            </p>
                            {audit.broken.length ? (
                                <ul className="mt-2 list-disc pl-5 text-xs text-amber-900 dark:text-amber-200" data-testid="grocery-atlas-broken-list">
                                    {audit.broken.slice(0, 40).map((b) => <li key={b}>{b}</li>)}
                                </ul>
                            ) : null}
                            {audit.missingSrc ? <p className="mt-1 text-xs text-amber-900">{audit.missingSrc} tile(s) have no icon mapped at all.</p> : null}
                        </>
                    )}
                </div>
            </header>

            <section className="mx-auto mt-8 max-w-6xl" data-testid="grocery-atlas-newest">
                <h2 className="text-base font-bold text-slate-900 dark:text-white md:text-lg">The five newest emoji</h2>
                <p className="mb-3 text-sm text-slate-600 dark:text-slate-400">These would be invisible on an older phone without the bundled image.</p>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                    {NEWEST.map((n) => (
                        <Tile key={n.file} name={n.label} icon={`${data.icon_base}/${n.file}`} testId={`grocery-atlas-newest-${n.file}`} />
                    ))}
                </div>
            </section>

            <section className="mx-auto mt-10 max-w-6xl">
                <h2 className="text-base font-bold text-slate-900 dark:text-white md:text-lg">Aisles · {data.counts.aisles}</h2>
                <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-6 lg:grid-cols-8" data-testid="grocery-atlas-aisles">
                    {sorted(data.aisles).map((a) => (
                        <Tile key={a.id} name={a.name[lang]} icon={a.icon} testId={`grocery-atlas-aisle-${a.id}`} />
                    ))}
                </div>
            </section>

            {sorted(data.aisles).map((a) => {
                const secs = sorted(data.sections.filter((s) => s.aisle_id === a.id));
                return (
                    <section key={a.id} className="mx-auto mt-10 max-w-6xl" data-testid={`grocery-atlas-aisle-block-${a.id}`}>
                        <h2 className="flex items-center gap-2 text-base font-bold text-slate-900 dark:text-white md:text-lg">
                            <GroceryIcon icon={a.icon} alt="" size={28} /> {a.name[lang]}
                            <span className="text-xs font-normal text-slate-500">{secs.length} sections</span>
                        </h2>
                        {secs.map((s) => (
                            <div key={s.key} className="mt-4">
                                <div className="mb-2 grid grid-cols-3 gap-3 sm:grid-cols-6 lg:grid-cols-8">
                                    <Tile name={s.name[lang]} icon={s.icon} note={s.shelf} testId={`grocery-atlas-section-${s.key}`} />
                                </div>
                                <div className="grid grid-cols-3 gap-3 sm:grid-cols-6 lg:grid-cols-8">
                                    {sorted(data.items.filter((i) => i.section_key === s.key)).map((i) => (
                                        <Tile key={i.id} name={i.name[lang]} icon={i.icon} badge={i.badge}
                                            note={i.sold_by === 'each' ? null : i.sold_by} status={i.icon_status}
                                            testId={`grocery-atlas-item-${i.id}`} />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </section>
                );
            })}

            <footer className="mx-auto mt-12 max-w-6xl pb-16 text-xs text-slate-500">
                Catalog version {data.version} · icons: Noto Emoji v2.047, PNG 128 px, bundled at {data.icon_base} ·
                licence and provenance in {data.icon_base}/NOTICE.md
            </footer>
        </div>
    );
}
